import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    Grid,
    Pagination,
    Card,
    CardContent,
    CardMedia,
    IconButton
} from '@mui/material';
import {
    Article,
    AutoStories,
    PlaylistPlay,
    VideoLibrary
} from '@mui/icons-material';
import { userRequest } from '../../requestMethods';
import PageContainer from '../layout/PageContainer';
import VideoPostCard from '../VideoPost/VideoPostCard';
import ShortStoryCard from '../ShortStory/ShortStoryCard';
import LongPostCard from '../LongPost/LongPostCard';

const PlaylistViewer = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);

    useEffect(() => {
        fetchPlaylistContent();
    }, [slug, page]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchPlaylistContent = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await userRequest.get(`/client-playlists/${slug}`, {
                params: { page, limit: 8 }
            });
            
            setContent(response.data);
        } catch (error) {
            console.error('Error fetching playlist content:', error);
            setError(error.response?.data?.error || 'Failed to load content');
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
        window.scrollTo(0, 0);
    };

    const getContentIcon = (contentType) => {
        const icons = {
            'ContentShortStory': <AutoStories />,
            'ContentLongPost': <Article />,
            'ContentVideoPost': <VideoLibrary />,
            'ContentPlaylist': <PlaylistPlay />
        };
        return icons[contentType] || <Article />;
    };



    const handleContentClick = (item) => {
        switch (item.contentType) {
            case 'ContentVideoPost':
                navigate(`/video-posts/${item._id}`);
                break;
            case 'ContentShortStory':
                navigate(`/story/${item._id}`);
                break;
            case 'ContentLongPost':
                // Long posts use slug, not _id
                navigate(`/long-posts/${item.slug}`);
                break;
            case 'ContentPlaylist':
                // For nested playlists, navigate to the playlist viewer with the playlist ID
                // This would need a different route structure if you want to show playlist children
                navigate(`/playlists/${item._id}`);
                break;
            default:
                console.warn('Unknown content type:', item.contentType);
        }
    };

    const renderContentCard = (item) => {
        switch (item.contentType) {
            case 'ContentVideoPost':
                return (
                    <VideoPostCard
                        key={item._id}
                        post={item}
                    />
                );
            
            case 'ContentShortStory':
                return (
                    <ShortStoryCard
                        key={item._id}
                        story={item}
                    />
                );
            
            case 'ContentLongPost':
                return (
                    <LongPostCard
                        key={item._id}
                        post={item}
                    />
                );
            
            case 'ContentPlaylist':
            default:
                // For playlists and unknown content types, render a generic card
                return (
                    <Card 
                        key={item._id} 
                        sx={{ 
                            height: '100%',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: 3
                            }
                        }}
                        onClick={() => handleContentClick(item)}
                    >
                        {item.thumbUrl && (
                            <CardMedia
                                component="img"
                                height="140"
                                image={item.thumbUrl}
                                alt={item.title}
                                sx={{ objectFit: 'cover' }}
                            />
                        )}
                        
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                <Typography 
                                    variant="h6" 
                                    component="h3"
                                    sx={{ 
                                        fontSize: '1rem',
                                        lineHeight: 1.3,
                                        display: '-webkit-box',
                                        overflow: 'hidden',
                                        WebkitBoxOrient: 'vertical',
                                        WebkitLineClamp: 2,
                                        flexGrow: 1,
                                        mr: 1
                                    }}
                                >
                                    {item.title}
                                </Typography>
                                
                                <IconButton 
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleContentClick(item);
                                    }}
                                >
                                    {getContentIcon(item.contentType)}
                                </IconButton>
                            </Box>

                            {item.summary && (
                                <Typography 
                                    variant="body2" 
                                    color="text.secondary"
                                    sx={{ 
                                        display: '-webkit-box',
                                        overflow: 'hidden',
                                        WebkitBoxOrient: 'vertical',
                                        WebkitLineClamp: 2,
                                        mb: 1
                                    }}
                                >
                                    {item.summary}
                                </Typography>
                            )}

                            {item.contentType === 'ContentPlaylist' && item.totalItems !== undefined && (
                                <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5 }}>
                                    {item.totalItems} items
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                );
        }
    };

    if (loading && !content) {
        return (
            <PageContainer>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                    <CircularProgress />
                </Box>
            </PageContainer>
        );
    }

    if (error) {
        return (
            <PageContainer>
                <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                </Alert>
            </PageContainer>
        );
    }

    if (!content) {
        return (
            <PageContainer>
                <Alert severity="info" sx={{ mt: 2 }}>
                    No content found
                </Alert>
            </PageContainer>
        );
    }

    return (
        <PageContainer title={content.title}>
            {content.description && (
                <Typography 
                    variant="body1" 
                    color="text.secondary" 
                    sx={{ mb: 3 }}
                >
                    {content.description}
                </Typography>
            )}

            {content.isPlaceholder ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                    This section is coming soon! We're working on bringing you amazing {content.title.toLowerCase()} content.
                </Alert>
            ) : content.message ? (
                <Alert severity="warning" sx={{ mt: 2 }}>
                    {content.message}
                </Alert>
            ) : content.items && content.items.length > 0 ? (
                <>
                    <Grid container spacing={3}>
                        {content.items.map((item) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={item._id}>
                                {renderContentCard(item)}
                            </Grid>
                        ))}
                    </Grid>

                    {/* Pagination */}
                    {content.totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                            <Pagination
                                count={content.totalPages}
                                page={content.currentPage}
                                onChange={handlePageChange}
                                color="primary"
                                size="large"
                            />
                        </Box>
                    )}

                    {/* Loading indicator for pagination */}
                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <CircularProgress />
                        </Box>
                    )}
                </>
            ) : (
                <Alert severity="info" sx={{ mt: 2 }}>
                    No content available in this category yet.
                </Alert>
            )}
        </PageContainer>
    );
};

export default PlaylistViewer;